<template>
  <div class="write_check-main glass-tab-page">
    <div class="write_check-header-bar">
      <BrucdcumHeader :breadcrumb="['Banking', 'Write Check']" />
    </div>

    <form class="write_check-form" @submit.prevent>
      <div class="glass-tab-view glass-tab-view--flush write_check-glass-shell">
        <div class="glass-tab-view__content write_check-tab-content">
          <!-- Vendor & Bank Details -->
          <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows write_check-section">
            <h3 class="glass-tab-panel__title">Vendor & Bank Details</h3>
            <div class="write_check-details-row write_check-form-row--columns glass-tab-panel__row">
              <div class="write_check-form-group write_check-form-group--label-dropdown">
                <LabelDropdown
                  label="Vendor"
                  select-id="write-check-vendor"
                  v-model="selectedVendor"
                  flex
                  wide
                  searchable
                  :options="vendorDropdownOptions"
                  @change="onVendorChange"
                />
              </div>
              <div class="write_check-form-group write_check-form-group--label-input">
                <LabelInputField
                  input-id="write-check-bank-account"
                  label="Bank Account"
                  :model-value="selectedBankAccountDisplay"
                  placeholder="Auto-detected from selected pay bill"
                  flex
                  readonly
                  disabled
                />
              </div>
            </div>
          </div>

          <!-- Vendor Pay Bills -->
          <div
            v-if="selectedVendor"
            class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows write_check-section"
          >
            <h3 class="glass-tab-panel__title">Vendor Pay Bills</h3>
            <div v-if="loading" class="write_check-loading">Loading pay bills...</div>
            <div v-if="inlineErrorMessage" class="write_check-alert glass-tab-panel__alert glass-tab-panel__alert--error">
              {{ inlineErrorMessage }}
            </div>

            <div v-if="vendorBills.length > 0" class="consultant-glass-table-section write_check-table-section">
              <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
                <div class="consultant-glass-table-scroll-x">
                  <table class="consultant-glass-table write_check-bills-table">
                    <thead>
                      <tr>
                        <th class="write_check-checkbox-col">Select</th>
                        <th>Pay Bill #</th>
                        <th>Date</th>
                        <th>Payment Method</th>
                        <th>Contact</th>
                        <th>Total Amount</th>
                        <th>Write Status</th>
                        <th>Print Status</th>
                        <th>Bills Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="bill in vendorBills" :key="bill.id">
                        <tr :class="{ selected: bill.selected }" class="consultant-glass-table-row">
                          <td class="write_check-checkbox-col">
                            <input
                              type="checkbox"
                              v-model="bill.selected"
                              class="write_check-checkbox"
                              @change="onBillSelection(bill)"
                            />
                          </td>
                          <td>{{ bill.billNumber }}</td>
                          <td>{{ formatDateToUS(bill.billDate) }}</td>
                          <td>{{ bill.paymentMethod || '-' }}</td>
                          <td>{{ bill.contactName || '-' }}</td>
                          <td>{{ formatCurrency(bill.billAmount) }}</td>
                          <td>
                            <span :class="['write_check-status-badge', bill.isWriteCheckDone ? 'write_check-status-done' : 'write_check-status-pending']">
                              {{ bill.isWriteCheckDone ? 'Done' : 'Pending' }}
                            </span>
                          </td>
                          <td>
                            <span :class="['write_check-status-badge', bill.isPrintCheckDone ? 'write_check-status-done' : 'write_check-status-pending']">
                              {{ bill.isPrintCheckDone ? 'Done' : 'Pending' }}
                            </span>
                          </td>
                          <td>
                            <span v-if="bill.records && bill.records.length > 0" class="write_check-records-count">
                              {{ bill.records.length }} bill(s) paid
                            </span>
                            <span v-else class="write_check-no-records">No bill records</span>
                          </td>
                        </tr>

                        <tr
                          v-if="bill.selected && bill.records && bill.records.length > 0"
                          class="write_check-sub-table-row"
                        >
                          <td colspan="9" class="write_check-sub-table-container">
                            <div class="write_check-bill-records-sub-table">
                              <h4 class="write_check-sub-table-title">Bill Payment Details</h4>
                              <table class="write_check-bill-records-table">
                                <thead>
                                  <tr>
                                    <th>Bill Number</th>
                                    <th>Bill Date</th>
                                    <th>Bill Total</th>
                                    <th>Amount Paid</th>
                                    <th>Remaining</th>
                                    <th>Status</th>
                                    <th>Memo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="record in bill.records" :key="record.id">
                                    <td class="write_check-bill-number-cell">
                                      {{ cleanBillNumber(record.bill?.billNo) || `Bill #${record.billId}` }}
                                    </td>
                                    <td>{{ formatDateToUS(record.bill?.date) }}</td>
                                    <td>{{ formatCurrency(record.bill?.total) }}</td>
                                    <td class="write_check-amount-paid-cell">{{ formatCurrency(record.amountPaid) }}</td>
                                    <td>{{ formatCurrency(record.bill?.remainingAmount) }}</td>
                                    <td>
                                      <span
                                        :class="[
                                          'write_check-status-badge',
                                          `write_check-status-${(record.bill?.status || '').toLowerCase()}`,
                                        ]"
                                      >
                                        {{ record.bill?.status || 'Unknown' }}
                                      </span>
                                    </td>
                                    <td class="write_check-memo-cell">
                                      {{ record.bill?.comments || bill.comments || '-' }}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                    <tfoot>
                      <tr class="write_check-total-row">
                        <td colspan="8" class="write_check-total-label">Total Payment:</td>
                        <td class="write_check-total-amount">{{ formatCurrency(totalPayment) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div
              v-if="vendorBills.length === 0 && selectedVendor && !loading && !inlineErrorMessage"
              class="write_check-no-bills"
            >
              No check-related pay bills found for this vendor
            </div>
          </div>

          <!-- Check Information -->
          <div
            v-if="totalPayment > 0"
            class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows write_check-section"
          >
            <h3 class="glass-tab-panel__title">Check Information</h3>
            <div class="write_check-check-info">
              <div class="write_check-meta-row">
                <div class="write_check-form-group write_check-form-group--label-date write_check-meta-field">
                  <LabelDateField
                    input-id="write-check-date"
                    label="Check Date"
                    v-model="checkDate"
                    flex
                  />
                </div>
                <div class="write_check-form-group write_check-form-group--label-input write_check-meta-field">
                  <LabelInputField
                    input-id="write-check-number"
                    label="Check Number"
                    v-model="checkNumber"
                    placeholder="TO BE PRINTED"
                    flex
                    readonly
                    disabled
                  />
                </div>
              </div>
              <div class="write_check-form-row write_check-form-row--single">
                <div class="write_check-form-group write_check-form-group--label-input">
                  <LabelInputField
                    input-id="write-check-payment-amount"
                    label="Payment Amount"
                    :model-value="formatCurrency(totalPayment)"
                    flex
                    readonly
                  />
                </div>
              </div>
              <div class="write_check-form-row write_check-form-row--single">
                <div class="write_check-form-group write_check-form-group--label-input">
                  <LabelInputField
                    input-id="write-check-amount-words"
                    label="Amount in Words"
                    :model-value="amountInWords"
                    flex
                    readonly
                  />
                </div>
              </div>
              <div class="write_check-form-row write_check-form-row--single">
                <div class="write_check-form-group write_check-form-group--label-input">
                  <LabelInputField
                    input-id="write-check-memo"
                    label="Memo"
                    v-model="memo"
                    placeholder="Optional memo for check"
                    flex
                  />
                </div>
              </div>
            </div>

            <div class="write_check-total-bar glass-tab-panel--nested">
              <span>Total Payment</span>
              <span class="write_check-total-amount">{{ formatCurrency(totalPayment) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="totalPayment > 0" class="write_check-form-actions glass-tab-view__actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" :disabled="loading" @click="cancel" />
        <SecondaryButton
          label="Save Draft Check"
          icon="bi bi-file-earmark"
          :disabled="loading"
          @click="saveDraft"
        />
        <PrimaryButton
          label="Confirm Check"
          icon="bi bi-check-lg"
          :disabled="loading || !selectedBankAccount"
          @click="confirmCheck"
        />
      </div>
    </form>

    <SuccessModal
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      :title="errorTitle"
      :message="modalErrorMessage"
      @close="closeErrorModal"
    />
  </div>
</template>

<script>
import '../styles/VendorWriteCheck.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import logger from '@/utils/logger';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import ApiService from '@/services/api.service.js';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import { isCheckPaymentMethod } from '../utils/vendorPaymentMethod.js';

export default {
  name: 'VendorWriteCheck',
  components: {
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    LabelInputField,
    LabelDropdown,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      selectedVendor: '',
      selectedBankAccount: '',
      vendors: [],
      bankAccounts: [],
      vendorBills: [],
      checkDate: '',
      checkNumber: 'TO BE PRINTED',
      memo: '',
      loading: false,
      inlineErrorMessage: '',
      modalErrorMessage: '',
      showSuccessModal: false,
      successTitle: '',
      successMessage: '',
      showErrorModal: false,
      errorTitle: 'Error',
    };
  },
  computed: {
    vendorDropdownOptions() {
      return [
        { value: '', label: 'Select Vendor' },
        ...(this.vendors || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.name,
        })),
      ];
    },
    totalPayment() {
      return this.vendorBills
        .filter((bill) => bill.selected)
        .reduce((sum, bill) => sum + (bill.paymentAmount || 0), 0);
    },
    amountInWords() {
      return this.numberToWords(this.totalPayment);
    },
    selectedBankAccountDisplay() {
      const selectedBill = this.vendorBills.find((bill) => bill.selected);
      if (selectedBill && selectedBill.payFromAccount) {
        const account = selectedBill.payFromAccount;
        return `${account.accountNumber || ''} - ${account.accountName || ''}`.replace(
          /^- |^$/,
          account.accountName || 'Unknown Account',
        );
      }
      return '';
    },
  },
  async mounted() {
    await this.loadInitialData();
    this.generateCheckNumber();
    this.checkDate = this.formatDateToUS(new Date());
  },
  methods: {
    async loadInitialData() {
      try {
        this.loading = true;
        const companyId = this.$route.params.id;

        const vendorData = await ApiService.getAllVendors(companyId);
        this.vendors = (Array.isArray(vendorData) ? vendorData : [])
          .filter((vendor) => vendor.active !== false)
          .map((vendor) => ({
            id: vendor.id,
            name: vendor.vendorName || vendor.name,
            companyId: vendor.companyId,
          }));

        const accountsResponse = await ApiService.get(`/accounts/company/${companyId}`);
        const allAccounts = accountsResponse.data?.data?.accounts || [];
        this.bankAccounts = allAccounts.filter((account) =>
          ['Bank', 'Cash', 'Checking', 'Savings'].includes(account.accountType),
        );
      } catch (error) {
        this.inlineErrorMessage = 'Failed to load initial data';
        logger.error('Error loading initial data:', error);
      } finally {
        this.loading = false;
      }
    },
    async onVendorChange() {
      if (!this.selectedVendor) {
        this.vendorBills = [];
        this.inlineErrorMessage = '';
        return;
      }

      try {
        this.loading = true;
        this.inlineErrorMessage = '';

        const response = await ApiService.get(
          `/vendor/pay-bill/vendor/${this.selectedVendor}?includeRecords=true`,
        );

        const payBillsData = response.data?.payBills || response.data || [];

        if (!Array.isArray(payBillsData)) {
          this.vendorBills = [];
          this.inlineErrorMessage = 'Invalid data format received from server. Please try again.';
          return;
        }

        const checkPayBills = payBillsData.filter(
          (payBill) => isCheckPaymentMethod(payBill.paymentMethod) && !payBill.isWriteCheckDone,
        );

        this.vendorBills = checkPayBills.map((payBill) => ({
          id: payBill.id,
          billNumber: payBill.no,
          billDate: payBill.date,
          dueDate: payBill.date,
          billAmount: payBill.total,
          balanceDue: payBill.total,
          paymentAmount: payBill.total,
          contactName: payBill.contactName,
          phone: payBill.phone,
          comments: payBill.comments,
          paymentMethod: payBill.paymentMethod,
          isWriteCheckDone: payBill.isWriteCheckDone,
          isPrintCheckDone: payBill.isPrintCheckDone,
          records: payBill.records || [],
          payFromAccount: payBill.payFromAccount || null,
          selected: false,
        }));

        this.inlineErrorMessage = '';
      } catch (error) {
        logger.error('Error loading vendor pay bills:', error);
        this.vendorBills = [];

        if (error.response) {
          const status = error.response.status;
          const serverMessage = error.response.data?.message || error.response.data?.error;

          if (status === 404) {
            this.inlineErrorMessage = 'No pay bills found for this vendor';
          } else if (status === 403) {
            this.inlineErrorMessage = "You do not have permission to access this vendor's pay bills.";
          } else if (status === 500) {
            this.inlineErrorMessage = 'Server error occurred while loading pay bills. Please try again later.';
          } else {
            this.inlineErrorMessage =
              serverMessage || `Failed to load vendor pay bills (Error ${status}). Please try again.`;
          }
        } else if (error.request) {
          this.inlineErrorMessage =
            'Network error: Unable to connect to server. Please check your internet connection.';
        } else {
          this.inlineErrorMessage = 'An unexpected error occurred while loading pay bills. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
    onBillSelection(bill) {
      if (!bill.selected) {
        bill.paymentAmount = 0;
      } else {
        bill.paymentAmount = bill.balanceDue;

        if (bill.payFromAccount && bill.payFromAccount.id) {
          this.selectedBankAccount = bill.payFromAccount.id;
        }
      }

      const hasSelectedBills = this.vendorBills.some((b) => b.selected);
      if (!hasSelectedBills) {
        this.selectedBankAccount = '';
      }

      this.onPaymentAmountChange();
    },
    onPaymentAmountChange() {
      this.$forceUpdate();
    },
    generateCheckNumber() {
      this.checkNumber = 'TO BE PRINTED';
    },
    async saveDraft() {
      try {
        this.loading = true;

        const checkData = {
          vendorId: this.selectedVendor,
          bankAccountId: this.selectedBankAccount,
          checkDate: this.checkDate,
          checkNumber: this.checkNumber,
          totalAmount: this.totalPayment,
          memo: this.memo,
          status: 'Draft',
          bills: this.vendorBills
            .filter((bill) => bill.selected)
            .map((bill) => ({
              billId: bill.id,
              paymentAmount: bill.paymentAmount,
            })),
        };

        await ApiService.post('/checks/draft', checkData);

        this.successTitle = 'Draft Saved';
        this.successMessage = 'Check has been saved as draft successfully.';
        this.showSuccessModal = true;
      } catch (error) {
        this.errorTitle = 'Save Failed';
        this.modalErrorMessage = 'Failed to save check as draft. Please try again.';
        this.showErrorModal = true;
        logger.error('Error saving draft:', error);
      } finally {
        this.loading = false;
      }
    },
    async confirmCheck() {
      if (!this.selectedBankAccount) {
        this.errorTitle = 'Validation Error';
        this.modalErrorMessage = 'Please select a bank account before confirming the check.';
        this.showErrorModal = true;
        return;
      }

      const selectedBills = this.vendorBills.filter((bill) => bill.selected);
      if (selectedBills.length === 0) {
        this.errorTitle = 'Validation Error';
        this.modalErrorMessage = 'Please select at least one pay bill to confirm.';
        this.showErrorModal = true;
        return;
      }

      try {
        this.loading = true;

        const checkData = {
          vendorId: this.selectedVendor,
          companyId: this.$route.params.id,
          bankAccountId: this.selectedBankAccount,
          checkDate: this.checkDate,
          checkNumber: this.checkNumber,
          totalAmount: this.totalPayment,
          memo: this.memo,
          bills: selectedBills.map((bill) => ({
            billId: bill.id,
          })),
        };

        const response = await ApiService.post('/vendor/pay-bill/confirm-check', checkData);

        if (response.data.success) {
          selectedBills.forEach((bill) => {
            bill.isWriteCheckDone = true;
            bill.selected = false;
          });

          this.successTitle = 'Check Confirmed';
          this.successMessage =
            response.data.message || 'Check has been confirmed and is ready to print.';
          this.showSuccessModal = true;
        } else {
          throw new Error(response.data.message || 'Confirmation failed');
        }
      } catch (error) {
        this.errorTitle = 'Confirmation Failed';
        this.modalErrorMessage =
          error.response?.data?.message ||
          error.message ||
          'Failed to confirm check. Please try again.';
        this.showErrorModal = true;
        logger.error('Error confirming check:', error);
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard`);
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successTitle = '';
      this.successMessage = '';
      this.resetForm();
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorTitle = 'Error';
      this.modalErrorMessage = '';
    },
    resetForm() {
      this.selectedVendor = '';
      this.selectedBankAccount = '';
      this.vendorBills = [];
      this.memo = '';
      this.inlineErrorMessage = '';
      this.generateCheckNumber();
    },
    formatDateToUS(date) {
      if (!date) return '';
      try {
        const d = new Date(date);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const year = d.getFullYear();
        return `${month}/${day}/${year}`;
      } catch (error) {
        return '';
      }
    },
    cleanBillNumber(billNo) {
      if (!billNo) return '';
      const cleanedBillNo = billNo.replace(/^.*?-BILL-/, '');
      return cleanedBillNo || billNo;
    },
    formatCurrency(value) {
      if (!value || isNaN(value)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(value);
    },
    numberToWords(num) {
      if (num === 0) return 'Zero Dollars';

      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
      const teens = [
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen',
      ];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

      function convertHundreds(n) {
        let result = '';
        if (n >= 100) {
          result += ones[Math.floor(n / 100)] + ' Hundred ';
          n %= 100;
        }
        if (n >= 20) {
          result += tens[Math.floor(n / 10)] + ' ';
          n %= 10;
        } else if (n >= 10) {
          result += teens[n - 10] + ' ';
          return result;
        }
        if (n > 0) {
          result += ones[n] + ' ';
        }
        return result;
      }

      let dollars = Math.floor(num);
      const cents = Math.round((num - dollars) * 100);

      let result = '';

      if (dollars >= 1000000) {
        result += convertHundreds(Math.floor(dollars / 1000000)) + 'Million ';
        dollars %= 1000000;
      }
      if (dollars >= 1000) {
        result += convertHundreds(Math.floor(dollars / 1000)) + 'Thousand ';
        dollars %= 1000;
      }
      if (dollars > 0) {
        result += convertHundreds(dollars);
      }

      result += dollars === 1 ? 'Dollar' : 'Dollars';

      if (cents > 0) {
        result += ' and ' + cents + '/100';
      }

      return result.trim();
    },
  },
};
</script>
